import { SparklesIcon } from "@heroicons/react/24/solid";
import { useI18N } from "@sundae/react-hooks";
import { Button, Dialog, Notice, Text, toolkitCx } from "@sundae/ui-toolkit";
import cx from "classnames";
import { AnimatePresence } from "framer-motion";
import { FC, FormEvent, Suspense } from "react";
import { Trans } from "react-i18next";

import { testIds } from "../../../constants/testIds.constants";
import { useMigrateLiquidityTx } from "../../../hooks/liquidity/useMigrateLiquidityTx";
import { Actions, useAppContext } from "../../../stores";
import { ETransactionState } from "../../../types/Transaction.types";
import LiquidityMigrationTransactionCollapsible from "../../LiquidityComponents/LiquidityMigrationTransactionCollapsible";
import LiquidityPoolMigrationList from "../../LiquidityComponents/LiquidityPoolMigrationList";
import LoadingComponent from "../../LoadingComponent";
import OrderSuccess from "../../OrderComponents/OrderSuccess";
import TransactionSubmittingWrapper from "../../TransactionSubmittingWrapper";
import ErrorBox from "./ErrorBox";
import { useMigrateLiquidityContext } from "./context";
import { EMigrationTransactionBuilderState } from "./types";

const MigrateLiquidityDialog: FC = () => {
  const { t } = useI18N("liquidity");
  const {
    state: {
      dialogs: { liquidity: liquidityDialogs },
    },
    dispatch,
  } = useAppContext();
  const {
    derived: { selectedPools },
    flowState: { dialogState },
    handlers: { resetContext, setDialogState },
    transaction: { builderState },
  } = useMigrateLiquidityContext();
  const { handleMigration } = useMigrateLiquidityTx();

  const onSubmit = (e: FormEvent<HTMLFormElement>) => {
    e.preventDefault();
    handleMigration.mutate();
  };

  const closeDialog = () => {
    resetContext();
    setDialogState(ETransactionState.initial);
    Actions.setDialogState({ dialog: "liquidity.migrate", isOpen: false }, dispatch);
  };

  const renderContent = () => {
    /* -------------------------------------------------------------------------------------------------
     * Initial State
     * -----------------------------------------------------------------------------------------------*/
    if (
      [
        ETransactionState.initial,
        ETransactionState.previewCancelled,
        ETransactionState.reset,
        ETransactionState.submittingCancelled,
      ].includes(dialogState)
    ) {
      return (
        <div className="relative z-10 flex h-full flex-col justify-between gap-4 rounded-lg">
          <div className="grid w-full gap-2">
            <Trans i18nKey="migrateLiquidity.intro" ns="liquidity">
              <Text tag="p" size="sm" variant="muted">
                The Sundae protocol has ratified the adoption of a new set of contracts.
              </Text>
              <Text tag="p" size="sm" variant="muted">
                These contracts bring huge scalability and efficiency improvements, as well as new
                opportunities for integrations with other protocols, and thus new revenue streams
                for liquidity providers.
              </Text>
              <Text tag="p" size="sm" variant="muted">
                Migrate your liquidity to the new contract with a single click, and let the
                blockchain do the rest!
              </Text>
            </Trans>
          </div>

          <form onSubmit={onSubmit} className="flex flex-col space-y-4">
            <div className="flex flex-col space-y-2">
              <LiquidityPoolMigrationList txState={dialogState} />
              <LiquidityMigrationTransactionCollapsible />
            </div>

            <div className="grid w-full gap-2 pt-4 lg:flex lg:items-center">
              <Button
                onClick={closeDialog}
                testId={testIds.yieldFarming.earnings.dialog.cancelButton}
                variant="neutral"
              >
                {t("cancel", { ns: "common" })}
              </Button>
              <Button
                type="submit"
                className="flex-1"
                disabled={
                  !selectedPools.length ||
                  builderState !== EMigrationTransactionBuilderState.SUCCESS
                }
              >
                {t("confirm", { ns: "common" })} & {t("liquidity", { context: "migrate" })}{" "}
                <SparklesIcon className={cx("h-4 w-5", toolkitCx.layout.text.warning)} />
              </Button>
            </div>
          </form>
        </div>
      );
    }

    /* -------------------------------------------------------------------------------------------------
     * Transaction Status (Error) UI
     * -----------------------------------------------------------------------------------------------*/
    if (
      builderState === EMigrationTransactionBuilderState.ERROR ||
      ETransactionState.error === dialogState
    ) {
      return (
        <Suspense fallback={<LoadingComponent size="medium" />}>
          <ErrorBox />
        </Suspense>
      );
    }

    /* -------------------------------------------------------------------------------------------------
     * Submitting UI
     * -----------------------------------------------------------------------------------------------*/
    if (dialogState === ETransactionState.submitting) {
      return (
        <TransactionSubmittingWrapper>
          <Notice className="mx-auto w-fit xl:mt-0" type="info" size="xs">
            {t("transaction.confirmInWallet", { ns: "transactions" })}
          </Notice>
          <div className="pointer-events-none">
            <LiquidityPoolMigrationList txState={dialogState} displaySelectPoolsOnly />
          </div>
        </TransactionSubmittingWrapper>
      );
    }

    /* -------------------------------------------------------------------------------------------------
     * Transaction Status (Success) UI
     * -----------------------------------------------------------------------------------------------*/
    if (ETransactionState.success === dialogState) {
      return (
        <OrderSuccess
          className="mb-4"
          message={t("migrateLiquidity.successMessage")}
          onCancel={closeDialog}
          onSuccess={closeDialog}
          onSuccessButtonText={t("close", { ns: "common" })}
        />
      );
    }
  };

  return (
    <Dialog
      cardContainerClassName={cx("lg:!w-[34rem]", toolkitCx.recipes.blurryCorner.secondary)}
      open={liquidityDialogs.migrate.isOpen}
      closeDialog={closeDialog}
      description={
        dialogState === ETransactionState.submitting
          ? t("migrateLiquidity.description", {
              context: "migrating",
            })
          : ""
      }
      title={t("liquidity", {
        context: dialogState === ETransactionState.submitting ? "migrating" : "migrate",
      })}
      withBlurryCorner={false}
      testId={testIds.migrateLiquidityDialog.content}
    >
      <AnimatePresence mode="wait">{renderContent()}</AnimatePresence>
    </Dialog>
  );
};

export default MigrateLiquidityDialog;
